/**
 * API helper for DDoS Guard endpoints.
 * Base: API_BASE + '/auth/ddosguard'
 */
import { apiFetch, apiUrl } from 'api/client';

const BASE = () => '/auth/ddosguard';

// ── Routers ────────────────────────────────────────────────────
export const fetchRouters = (organizationId) => {
    const params = new URLSearchParams();
    if (organizationId) params.set('organization_id', String(organizationId));
    const qs = params.toString();
    return apiFetch(`${BASE()}/routers${qs ? `?${qs}` : ''}`).then(r => r.json());
};

export const fetchRouter = (id) =>
    apiFetch(`${BASE()}/routers/${encodeURIComponent(id)}`).then(r => r.json());

export const fetchRouterRules = (id) =>
    apiFetch(`${BASE()}/routers/${encodeURIComponent(id)}/rules`).then(r => r.json());

// ── Filters ────────────────────────────────────────────────────
export const fetchFilters = (routerId, organizationId) => {
    const params = new URLSearchParams();
    if (routerId) params.set('router_id', routerId);
    if (organizationId) params.set('organization_id', String(organizationId));
    const qs = params.toString();
    return apiFetch(`${BASE()}/filters${qs ? `?${qs}` : ''}`).then(r => r.json());
};

export const deleteFilter = async (routerId, ruleId, filterId) => {
    const params = new URLSearchParams({
        router_id: String(routerId),
        rule_id: String(ruleId),
        filter_id: String(filterId),
    });

    const res = await apiFetch(`${BASE()}/filters?${params.toString()}`, {
        method: 'DELETE',
    });

    let body = null;
    try {
        body = await res.json();
    } catch {
        body = null;
    }

    if (!res.ok) {
        const err = new Error(body?.error || `Failed to delete filter (${res.status})`);
        err.status = res.status;
        throw err;
    }

    return body || { queued: true };
};

export const deleteLogs = async ({ target = 'all', routerId, ruleId, filterId, eventIds } = {}) => {
    const params = new URLSearchParams();
    if (target) params.set('target', String(target));
    if (routerId) params.set('router_id', String(routerId));
    if (ruleId) params.set('rule_id', String(ruleId));
    if (filterId != null) params.set('filter_id', String(filterId));
    const ids = (Array.isArray(eventIds) ? eventIds : eventIds != null ? [eventIds] : [])
        .map(id => Number(id))
        .filter(id => Number.isInteger(id) && id > 0);
    if (ids.length > 0) params.set('event_ids', ids.join(','));

    const res = await apiFetch(`${BASE()}/logs?${params.toString()}`, {
        method: 'DELETE',
    });

    let body = null;
    try {
        body = await res.json();
    } catch {
        body = null;
    }

    if (!res.ok) {
        const err = new Error(body?.error || `Failed to delete logs (${res.status})`);
        err.status = res.status;
        throw err;
    }

    return body || { target, deleted_events: 0, deleted_filters: 0 };
};

export const fetchFilterReport = async ({
    routerId,
    ruleId,
    filterId,
    startedAt,
    stepSeconds = 1,
    includeSeries = true,
} = {}) => {
    const params = new URLSearchParams({
        router_id: String(routerId),
        rule_id: String(ruleId),
        filter_id: String(filterId),
    });

    if (startedAt) params.set('started_at', String(startedAt));
    if (Number.isFinite(Number(stepSeconds))) params.set('step_seconds', String(stepSeconds));
    params.set('include_series', includeSeries ? 'true' : 'false');

    const res = await apiFetch(`${BASE()}/filter-report?${params.toString()}`, {
        method: 'GET',
    });

    let body = null;
    try {
        body = await res.json();
    } catch {
        body = null;
    }

    if (!res.ok) {
        const err = new Error(body?.error || `Failed to load filter report (${res.status})`);
        err.status = res.status;
        throw err;
    }

    return body || {};
};

// ── Events ─────────────────────────────────────────────────────
export const fetchEvents = ({ organizationId, routerId, ruleId, eventType, since, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (organizationId) params.set('organization_id', String(organizationId));
    if (routerId) params.set('router_id', routerId);
    if (ruleId) params.set('rule_id', ruleId);
    if (eventType) params.set('event_type', eventType);
    if (since) params.set('since', since);
    if (limit) params.set('limit', String(limit));
    return apiFetch(`${BASE()}/events?${params}`).then(r => r.json());
};

export const fetchEventFilters = ({ organizationId, routerId, ruleId, since, limit = 100 } = {}) => {
    const params = new URLSearchParams();
    if (organizationId) params.set('organization_id', String(organizationId));
    if (routerId) params.set('router_id', routerId);
    if (ruleId) params.set('rule_id', ruleId);
    if (since) params.set('since', since);
    if (limit) params.set('limit', String(limit));
    return apiFetch(`${BASE()}/events-filters?${params}`).then(r => r.json());
};

// ── SSE stream URL ─────────────────────────────────────────────
export const streamUrl = (organizationId) => {
    const params = new URLSearchParams();
    if (organizationId) params.set('organization_id', String(organizationId));
    const qs = params.toString();
    return apiUrl(`${BASE()}/stream${qs ? `?${qs}` : ''}`);
};
